import { DetailEditableField } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/DetailEditableField.tsx';
import { SecurityParticipationThresholdFormDto } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/ParticipationThreshold/participationThreshold.zod.ts';
import {
	PARTICIPATION_THRESHOLD_FORM_FIELD,
	getSecurityParticipationThresholdBasisTranslation,
	useParticipationThresholdController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/ParticipationThreshold/useParticipationThresholdController.ts';
import {
	RelativeRank,
	SecurityFeatureBaseProps,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/securityFeatures.utils.ts';
import { EditableInfoContainer } from '@dealstack/frontend/shared/components/EditableInfoContainer/EditableInfoContainer.tsx';
import { EditableInfoContainerGrid } from '@dealstack/frontend/shared/components/EditableInfoContainer/styled.tsx';
import { EditableInfoField } from '@dealstack/frontend/shared/components/EditableInfoField/EditableInfoField.tsx';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { updateSecurityParticipationThreshold } from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { useMutation } from '@tanstack/react-query';
import { PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Select } from 'react-hook-form-mantine';

import { SecurityRankField } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityRankField/SecurityRankField.tsx';
import { AuditContext } from '@dealstack/frontend/shared/components/Audit/useAuditController.ts';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { SecurityParticipationThresholdBasis } from '@dealstack/shared/lib/generated';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

export const ParticipationThreshold = ({
	security,
	onCancel,
	refreshDetails,
	hasEditorPermission = true,
	relativeRanksMap,
}: PropsWithChildren<
	SecurityFeatureBaseProps & { relativeRanksMap?: Map<string, RelativeRank> }
>) => {
	const tCommonUi = useTranslationCommonUi();
	const tCapManage = useTranslationCapManage();
	const { participationThresholdForm, resetForm } = useParticipationThresholdController(
		security,
		tCapManage
	);
	const { refetchSecurityAudit } = useContext(AuditContext);

	const [isEdit, setIsEdit] = useState(!security?.residueParticipationThreshold);
	const [deleteDrawerOpen, setDeleteDrawerOpen] = useState(false);

	const updateParticipationThresholdRequest = useMutation({
		mutationFn: updateSecurityParticipationThreshold,
	});

	const isLoading = updateParticipationThresholdRequest.isPending;
	const watchedBasis = participationThresholdForm.watch(
		PARTICIPATION_THRESHOLD_FORM_FIELD.residueParticipationThresholdBasis
	);
	const disableBasisTier =
		watchedBasis !== SecurityParticipationThresholdBasis.TOTAL_PROCEEDS_WITHIN_TIER;

	useEffect(() => {
		if (disableBasisTier) {
			participationThresholdForm.setValue(
				PARTICIPATION_THRESHOLD_FORM_FIELD.participationThresholdBasisTier,
				null
			);
		}
	}, [disableBasisTier]);

	const rankDropdownOptions = useMemo(() => {
		if (!relativeRanksMap) {
			return [];
		}
		return [...relativeRanksMap.values()]
			.sort((a, b) => b.sortOrder - a.sortOrder)
			.map((rank) => ({
				value: rank.id,
				label: `${rank.displayName} (${rank.relativeSortOrder})`,
			}));
	}, [relativeRanksMap]);

	const selectedRank = relativeRanksMap?.get(security?.participationThresholdBasisTierId ?? '');

	const handleUpdateParticipationThreshold = (args: SecurityParticipationThresholdFormDto) => {
		const securityId = security?.id;

		if (!securityId) {
			throw Error('Expect securityId to be defined');
		}

		const participationThresholdBasisTierId =
			args?.residueParticipationThresholdBasis !==
			SecurityParticipationThresholdBasis.TOTAL_PROCEEDS_WITHIN_TIER
				? null
				: args.participationThresholdBasisTier || null;

		updateParticipationThresholdRequest.mutate(
			{
				securityId,
				body: {
					residueParticipationThreshold: true,
					residueParticipationThresholdBasis: args.residueParticipationThresholdBasis,
					participationThresholdBasisTierId,
				},
			},
			{
				onSuccess: () => {
					showToastV2({
						title: tCapManage(
							'toasts.success.participation_threshold_updated_successfully'
						),
					});

					refreshDetails?.(securityId);
					void refetchSecurityAudit();
					setIsEdit(false);
				},
			}
		);
	};
	const handleDeleteParticipationThreshold = () => {
		const securityId = security?.id;

		if (!securityId) {
			throw Error('Expect securityId to be defined');
		}

		updateParticipationThresholdRequest.mutate(
			{
				securityId,
				body: {
					residueParticipationThreshold: false,
					residueParticipationThresholdBasis:
						SecurityParticipationThresholdBasis.TOTAL_SOURCES_FOR_ISSUER,
					participationThresholdBasisTierId: null,
				},
			},
			{
				onSuccess: () => {
					showToastV2({
						title: tCapManage(
							'toasts.success.participation_threshold_deleted_successfully'
						),
					});
					refreshDetails?.(securityId);
					void refetchSecurityAudit();
					setIsEdit(false);
				},
			}
		);
	};

	const handleCancel = () => {
		resetForm();
		onCancel?.();
	};

	return (
		<>
			<EditableInfoContainer
				title={tCapManage('securities.participationThreshold.title')}
				isEdit={isEdit}
				setIsEdit={setIsEdit}
				disableButtons={isLoading}
				onUpdate={participationThresholdForm.handleSubmit(
					handleUpdateParticipationThreshold
				)}
				onRemove={() => setDeleteDrawerOpen(true)}
				onCancel={handleCancel}
				hideRemove={!security?.residueParticipationThreshold}
				hasEditorPermission={hasEditorPermission}
				updateBtnDataTestId={
					TEST_ELEMENT_IDS.CAP_SECURITY_DETAILS_FEATURE_ROW_UPDATE_BUTTON
				}
			>
				<FormProvider {...participationThresholdForm}>
					{isLoading && <InlineLoader />}
					<form
						onSubmit={participationThresholdForm.handleSubmit(
							handleUpdateParticipationThreshold
						)}
						style={{ width: '100%' }}
					>
						<EditableInfoContainerGrid $gridColumns={4} $alignItems="start">
							<DetailEditableField
								editMode={isEdit}
								label={tCapManage('securities.participationThreshold.basis.label')}
								formInput={
									<Select
										name={
											PARTICIPATION_THRESHOLD_FORM_FIELD.residueParticipationThresholdBasis
										}
										control={participationThresholdForm.control}
										size="md"
										rightSection={<Icon variant="caret-down" size="lg" />}
										rightSectionProps={{
											style: { pointerEvents: 'none' },
										}}
										data={makeSecurityParticipationThresholdBasisDropdownOptions(
											tCommonUi
										).map((o) => ({ value: o.value, label: o.label ?? '' }))}
										data-testid={
											TEST_ELEMENT_IDS.CAP_PARTICIPATION_THRESHOLD_BASIS_SELECT
										}
									/>
								}
								data={
									getSecurityParticipationThresholdBasisTranslation(
										tCommonUi,
										security?.residueParticipationThresholdBasis
									) || LONG_DASH
								}
							/>

							<EditableInfoField
								editMode={isEdit}
								label={tCapManage(
									'securities.participationThreshold.participationThreshold.tier.label'
								)}
								formInput={
									<Select
										name={
											PARTICIPATION_THRESHOLD_FORM_FIELD.participationThresholdBasisTier
										}
										control={participationThresholdForm.control}
										disabled={disableBasisTier}
										size="md"
										rightSection={<Icon variant="caret-down" size="lg" />}
										rightSectionProps={{
											style: { pointerEvents: 'none' },
										}}
										data={rankDropdownOptions}
										data-testid={
											TEST_ELEMENT_IDS.CAP_PARTICIPATION_THRESHOLD_TIER_INPUT
										}
									/>
								}
								customValueElement={
									disableBasisTier || !selectedRank ? (
										<div>{LONG_DASH}</div>
									) : (
										<SecurityRankField
											rankName={selectedRank.displayName}
											sortOrder={selectedRank.relativeSortOrder}
											totalRanks={relativeRanksMap?.size}
											showContainer={false}
										/>
									)
								}
							/>
						</EditableInfoContainerGrid>
					</form>
				</FormProvider>
			</EditableInfoContainer>
			<ConfirmDeleteDrawer
				opened={deleteDrawerOpen}
				onClose={() => setDeleteDrawerOpen(false)}
				title={tCapManage('securities.participationThreshold.delete_drawer.title')}
				inputLabel={tCapManage(
					'securities.participationThreshold.delete_drawer.inputLabel'
				)}
				inputPlaceholder={tCapManage(
					'securities.participationThreshold.delete_drawer.inputPlaceholder'
				)}
				hideAlertIcon={true}
				confirmDeleteText="delete"
				wordMismatchErrorMessage={tCapManage(
					'securities.participation_threshold.delete_drawer.word_mismatch_error'
				)}
				deleteBtnText={tCapManage(
					'securities.participationThreshold.delete_drawer.confirmDelete'
				)}
				alertTitle={tCapManage(
					'securities.participationThreshold.delete_drawer.alertTitle'
				)}
				alertDescription={tCapManage(
					'securities.participationThreshold.delete_drawer.alertDescription'
				)}
				cancelBtnText={tCapManage('securities.participationThreshold.delete_drawer.cancel')}
				deleteLoading={isLoading}
				deleteSuccess={updateParticipationThresholdRequest.isSuccess}
				handleDelete={handleDeleteParticipationThreshold}
			/>
		</>
	);
};
export const makeSecurityParticipationThresholdBasisDropdownOptions = (
	tCommonUI: TranslateFunction<I18Namespaces.COMMON_UI>
) =>
	Object.values(SecurityParticipationThresholdBasis).map((value) => ({
		value,
		label: getSecurityParticipationThresholdBasisTranslation(tCommonUI, value),
	}));
